import { MessageCircle, Phone, Navigation, Star } from "lucide-react";
import { Reveal } from "./Reveal";
import { Logo, LogoMark } from "./Logo";
import { Greeting } from "./Greeting";
import { SITE, waLink, telLink, WA } from "../config/site";
import { scrollTo } from "./Navbar";

const QUICK = [
  { label: "Categories", href: "#categories" },
  { label: "Offers", href: "#offers" },
  { label: "Our Store", href: "#store" },
  { label: "Alterations", href: "#alterations" },
  { label: "Brands", href: "#brands" },
  { label: "Delivery", href: "#delivery" },
  { label: "Visit Us", href: "#visit" },
  { label: "Reviews", href: "#reviews" },
  { label: "FAQ", href: "#faq" },
];

export const FinalCta = () => (
  <section className="bg-gold text-charcoal relative overflow-hidden">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center relative">
      <Reveal>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-balance leading-[1.05]">
          Looking for Something? <span className="italic font-medium">Just Ask.</span>
        </h2>
        <p className="mt-6 text-base sm:text-lg text-charcoal/70 leading-relaxed max-w-2xl mx-auto">
          Whether you need everyday clothing, footwear, innerwear, accessories,
          alterations or home-comfort essentials — get in touch with Om Sri Sri
          Collection.
        </p>
      </Reveal>
      <Reveal delay={0.12}>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <a
            href={waLink(WA.general)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="final-whatsapp-button"
            className="inline-flex items-center gap-2.5 bg-charcoal text-cream font-semibold rounded-full px-8 py-4 hover:bg-black transition-all duration-300 hover:-translate-y-0.5"
          >
            <MessageCircle size={18} /> WhatsApp Us
          </a>
          <a
            href={telLink}
            data-testid="final-call-button"
            className="inline-flex items-center gap-2.5 border-2 border-charcoal/70 text-charcoal font-semibold rounded-full px-8 py-4 hover:bg-charcoal hover:text-cream transition-all duration-300 hover:-translate-y-0.5"
          >
            <Phone size={17} /> Call Now
          </a>
          <a
            href={SITE.mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="final-directions-button"
            className="inline-flex items-center gap-2.5 border-2 border-charcoal/70 text-charcoal font-semibold rounded-full px-8 py-4 hover:bg-charcoal hover:text-cream transition-all duration-300 hover:-translate-y-0.5"
          >
            <Navigation size={17} /> Get Directions
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

export const Footer = () => (
  <footer className="bg-charcoal border-t border-white/10">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="grid md:grid-cols-3 gap-10">
        <div>
          <Logo />
          <p className="mt-5 text-sm text-cream/45 leading-relaxed max-w-xs">
            Family clothing, footwear, accessories, innerwear and home-comfort
            essentials — with in-house boutique & alteration services for men
            and women.
          </p>
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
            Explore
          </p>
          <ul className="mt-5 space-y-3">
            {QUICK.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollTo(l.href);
                  }}
                  className="text-sm text-cream/55 hover:text-gold transition-colors duration-300"
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
            Reach Us
          </p>
          <ul className="mt-5 space-y-3 text-sm text-cream/55">
            <li data-testid="footer-address">
              {SITE.addressLine1},<br />
              {SITE.addressLine2}
            </li>
            <li>
              <a href={telLink} data-testid="footer-phone" className="hover:text-gold transition-colors">
                {SITE.phoneDisplay}
              </a>
            </li>
            <li data-testid="footer-hours">{SITE.hours}</li>
            <li>
              <a
                href={SITE.reviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-review-link"
                className="inline-flex items-center gap-1.5 text-gold/90 hover:text-gold transition-colors"
              >
                <Star size={13} fill="currentColor" /> Leave us a Google review
              </a>
            </li>
            <li className="text-gold/90 font-medium">
              Free delivery within 5 km · Orders above ₹1,500
            </li>
          </ul>
        </div>
      </div>
      <div className="mt-12 pt-7 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-xs text-cream/35">
          © 2026 {SITE.name}. All rights reserved.
        </p>
        <div className="flex items-center gap-2 text-xs text-cream/35">
          <LogoMark size={18} />
          <Greeting className="text-gold/80 text-xs" />
        <span className="hidden sm:inline">· Welcome in every Indian language.</span>
        </div>
      </div>
    </div>
  </footer>
);
